import api from 'api/api'
import { Button, Card, FlexView, Icon, LoadingOverlay } from 'components/common'
import FilterableServerLoadTable from 'components/common/FilterableServerLoadTable'
import { useWindowSize } from 'hooks/useWindowSize'
import moment from 'moment'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AppContext } from 'stores/AppStore'
import { PipeContext } from 'stores/PipeStore'
import { UserContext } from 'stores/UserStore'
import styled from 'styled-components'
import { LOAD_DATA_TYPE, loadStatusColor, loadStatusIcon, REFERENCE_SCREEN_SIZE } from 'utils/constants'

const BackButton = styled(Button)`
    font-family: 'Roboto';
    background: none;
    border-radius: 0px;
    border: none;
    box-shadow: none;

    color: gray;
    font-weight: bold;
    margin: 0px;

    &:active {
        opacity: 0.4;
    }
`
const IconBack = styled(Icon)`
    width: 24px;
    height: 24px;
    margin: 0px 16px 0px 0px;
`
const FlexPage = styled(FlexView)`
    align-self: stretch;
    align-items: flex-start;
    margin: 0px 0px 0px 10px;
    width: calc(100% - 32px);
`

// No client-side sorting; server returns sorted data
const formatLoads = (loads) => loads
const getDeadSpace = (height) => (height > REFERENCE_SCREEN_SIZE ? 550 : 450)

const Loads = () => {
    const { height } = useWindowSize(0)
    const history = useHistory()
    const { t } = useTranslation()

    const { idToken } = useContext(UserContext)
    const { setPageSubtitle, setPageLoading, pageLoading } = useContext(AppContext)
    const { setLoads, loads, setStatus, setIdLoadData, setIdLoad } = useContext(PipeContext)

    const [deadSpace, setDeadSpace] = useState(0)

    const PAGE_SIZE = 50

    const [pageIndex, setPageIndex] = useState(0)
    const [pageSize, setPageSize] = useState(PAGE_SIZE)
    const [totalCount, setTotalCount] = useState(0)
    const lastFetchKeyRef = useRef({ index: null, size: null })
    const fetchTimeoutRef = useRef(null)
    const initTokenRef = useRef(null)
    const [sortBy, setSortBy] = useState('id_load')
    const [sortOrder, setSortOrder] = useState('desc')
    const [filters, setFilters] = useState([])
    const filtersCountTimeoutRef = useRef(null)

    // Helper: map client filters to server filters (handles id_load_data logic and date offsets)
    const mapServerFilters = useCallback((filtersList) => {
        const mappedFilters = (filtersList || []).reduce((acc, f) => {
            if (f.id === 'id_load_data') {
                const raw = (f.value ?? '').toString().trim()
                if (!raw) {
                    return acc
                }
                const input = raw.toLowerCase()
                const nominalText = (t('Nominal Data') || '').toLowerCase()
                const actualText = (t('Actual Data') || '').toLowerCase()
                const matchesNominal = nominalText.includes(input)
                const matchesActual = actualText.includes(input)
                if (matchesNominal && matchesActual) {
                    // Both match: do not filter by type
                    return acc
                } else if (matchesNominal) {
                    acc[f.id] = LOAD_DATA_TYPE.NOMINAL_DATA
                } else if (matchesActual) {
                    acc[f.id] = LOAD_DATA_TYPE.PIPE_DATA
                } else {
                    acc[f.id] = LOAD_DATA_TYPE.UNDEFINED
                }
                return acc
            }
            acc[f.id] = f.value
            return acc
        }, {})

        const dateColumns = ['load_date', 'validation_date']
        const filtersTimeDiff = {}
        dateColumns.forEach(id => {
            const f = (filtersList || []).find(f => f.id === id && !!f.value)
            if (f) {
                const localOffset = moment(f.value).utcOffset()
                filtersTimeDiff[id] = localOffset
            }
        })

        return { filters: mappedFilters, filtersTimeDiff }
    }, [t])

    useEffect(() => {
        setDeadSpace(getDeadSpace(height))
    }, [height])

    useEffect(() => {
        setPageSubtitle(' - ' + t('Data Validation'))
    }, [setPageSubtitle, t])

    const redirect = useCallback(
        (path) => {
            history.push(path)
        },
        [history],
    )

    const fetchPage = useCallback(
        async (index = 0) => {
            try {
                // Dedupe identical fetches triggered close together
                const filtersKey = JSON.stringify((filters || [])
                    .map(f => ({ id: f.id, value: f.value }))
                    .sort((a, b) => a.id.localeCompare(b.id)))
                const currentKey = { index, size: pageSize, sortBy, sortOrder, filtersKey }
                const lastKey = lastFetchKeyRef.current
                if (
                    lastKey &&
                    lastKey.index === currentKey.index &&
                    lastKey.size === currentKey.size &&
                    lastKey.sortBy === currentKey.sortBy &&
                    lastKey.sortOrder === currentKey.sortOrder &&
                    lastKey.filtersKey === currentKey.filtersKey
                ) {
                    return
                }
                setPageLoading(true)
                // Build filters payload and include per-date time difference (local vs UTC)
                const { filters: filterValues, filtersTimeDiff } = mapServerFilters(filters || [])
                const result = await api.getDataLoadsWithLimitAndOffset(
                    {
                        limit: pageSize,
                        offset: index * pageSize,
                        sortBy,
                        sortOrder,
                        filters: filterValues,
                        filtersTimeDiff,
                    },
                    idToken,
                )
                const data = result || []
                setLoads(formatLoads(data))
                lastFetchKeyRef.current = currentKey
            } catch (error) {
                toast.error(error.message)
                setLoads([])
            } finally {
                setPageLoading(false)
            }
        },
        [pageSize, sortBy, sortOrder, filters, idToken, setLoads, setPageLoading, mapServerFilters],
    )

    const scheduleFetch = useCallback(
        (index = 0) => {
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current)
            }
            fetchTimeoutRef.current = setTimeout(() => {
                fetchPage(index)
            }, 150)
        },
        [fetchPage],
    )

    useEffect(() => () => {
        if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current)
        }
    }, [])

    useEffect(() => {
        if (!idToken) return
        if (initTokenRef.current === idToken) return
        initTokenRef.current = idToken

        async function fetchData() {
            try {
                setPageLoading(true)
                setIdLoad(null)
                setIdLoadData(null)
                setLoads([])
                // Get total count once
                const result = await api.getTotalAmountOfLoads({}, idToken)
                const count = result?.count || 0
                setTotalCount(count)
                // Initialize pagination state and let table trigger first fetch via onPageChange
                setPageIndex(0)
            } catch (error) {
                toast.error(error.message)
                setPageLoading(false)
            }
        }
        fetchData()
    }, [idToken, setIdLoad, setIdLoadData, setLoads, setPageLoading])

    const onPageChange = useCallback(
        (nextPageIndex) => {
            setPageIndex(nextPageIndex)
            scheduleFetch(nextPageIndex)
        },
        [scheduleFetch],
    )

    const onPageSizeChange = useCallback(
        (nextPageSize) => {
            setPageSize(nextPageSize)
            setPageIndex(0)
            scheduleFetch(0)
        },
        [scheduleFetch],
    )

    const onSortChange = useCallback(
        (sort) => {
            if (!sort) {
                // default sort
                setSortBy('id_load')
                setSortOrder('desc')
            } else {
                setSortBy(sort.id)
                setSortOrder(sort.desc ? 'desc' : 'asc')
            }
            setPageIndex(0)
            scheduleFetch(0)
        },
        [scheduleFetch],
    )

    const onFiltersChange = useCallback(
        (nextFilters) => {
            setFilters(nextFilters)
            setPageIndex(0)
            scheduleFetch(0)
            // Also refresh totalCount based on current filters
            if (filtersCountTimeoutRef.current) {
                clearTimeout(filtersCountTimeoutRef.current)
            }
            filtersCountTimeoutRef.current = setTimeout(async () => {
                try {
                    const { filters: mapped, filtersTimeDiff } = mapServerFilters(nextFilters || [])
                    const res = await api.getTotalAmountOfLoads({ filters: mapped, filtersTimeDiff }, idToken)
                    const cnt = res?.count || 0
                    setTotalCount(cnt)
                } catch (e) {
                    toast.error(e.message)
                }
            }, 150)
        },
        [scheduleFetch, idToken, mapServerFilters],
    )

    const onButtonClick = useCallback(
        async (idLoad, status, idLoadData) => {
            setStatus(status)
            setIdLoad(idLoad)
            setIdLoadData(idLoadData)
            redirect('/validation/details')
        },
        [setStatus, setIdLoad, setIdLoadData, redirect],
    )

    const columns = useMemo(
        () => [
            {
                id: 'id_load',
                accessor: 'id_load',
                Header: t('Id'),
            },
            {
                id: 'id_load_data',
                accessor: ({ id_load_data }) =>
                    id_load_data === LOAD_DATA_TYPE.NOMINAL_DATA ? t('Nominal Data') : t('Actual Data'),
                Header: t('Load Type'),
            },
            {
                id: 'load_title',
                accessor: 'load_title',
                Header: t('Load Title'),
            },
            {
                id: 'load_status_description',
                accessor: 'load_status_description',
                Header: t('Load Status'),
                Cell: ({ cell: { value, row } }) => (
                    <FlexView alignItems="center" justifyContent="flex-start" width="100%" flexDirection="row">
                        <font size="2"> {t(value)} </font>
                        <Icon
                            name={loadStatusIcon[row.original.id_load_status]}
                            width="16px"
                            height="16px"
                            margin="0px 4px 0px 8px"
                            color={loadStatusColor[row.original.id_load_status]}
                        />
                    </FlexView>
                ),
            },
            {
                id: 'cod_client',
                accessor: 'cod_client',
                Header: t('End User'),
            },
            {
                id: 'load_owner',
                accessor: 'load_owner',
                Header: t('Load Owner'),
            },
            {
                id: 'load_date',
                accessor: ({ load_date }) => (load_date ? moment(load_date).format('YYYY-MM-DD HH:mm:ss') : ''),
                Header: t('Load Date'),
            },
            {
                id: 'validation_date',
                accessor: ({ validation_date }) =>
                    validation_date ? moment(validation_date).format('YYYY-MM-DD HH:mm:ss') : '',
                Header: t('Validation Date'),
            },
            {
                id: 'remarks',
                accessor: 'remarks',
                Header: t('Remarks'),
            },
            {
                id: 'created_amount',
                accessor: 'created_amount',
                Header: t('Total Created'),
                Cell: ({ cell: { value, row } }) => (
                    <FlexView alignItems="center" justifyContent="flex-start" width="100%" flexDirection="row">
                        <font size="2">{row.original.created_amount}</font>
                    </FlexView>
                ),
            },
            {
                id: 'updated_amount',
                accessor: 'updated_amount',
                Header: t('Total Updated'),
                Cell: ({ cell: { value, row } }) => (
                    <FlexView alignItems="center" justifyContent="flex-start" width="100%" flexDirection="row">
                        <font size="2"> {row.original.updated_amount}</font>
                    </FlexView>
                ),
            },
            {
                id: 'details',
                Header: '',
                disableFilters: true,
                disableSortBy: true,
                customHeaderProps: {
                    style: {
                        minWidth: '32px',
                    },
                },
                Cell: ({ cell: { value, row } }) => (
                    <FlexView alignItems="center" justifyContent="center" width="100%">
                        <Button
                            fontSize="12px"
                            padding="4px 8px"
                            margin="0px 0px 0px 8px"
                            color="secondary"
                            onClick={() => {
                                onButtonClick(
                                    row.original.id_load,
                                    row.original.id_load_status,
                                    row.original.id_load_data,
                                )
                            }}
                        >
                            {t('Details')}
                        </Button>
                    </FlexView>
                ),
            },
        ],
        [t, onButtonClick],
    )

    return (
        <FlexPage>
            <LoadingOverlay visible={pageLoading} />
            <BackButton flexDirection="row" onClick={() => history.push('/')} fontSize="subtitle">
                <IconBack name="arrow-left" />
                <div> {t('Go Back')} </div>
            </BackButton>
            <Card alignSelf="stretch" justifyContent="flex-start" alignItems="center" padding="0.5rem 1rem">
                <FilterableServerLoadTable
                    columns={columns}
                    data={loads}
                    deadSpace={deadSpace}
                    // Server-side pagination props
                    serverSide
                    totalCount={totalCount}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    onPageChange={onPageChange}
                    onPageSizeChange={onPageSizeChange}
                    onSortChange={onSortChange}
                    onFiltersChange={onFiltersChange}
                />
            </Card>
        </FlexPage>
    )
}

export default Loads
